import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  type CSSProperties,
} from 'react';
import { cn } from '@/lib/utils';
import type { ReportMentionItem } from './reportMentionTypes';

export type ReportMentionSuggestionListProps = {
  items: ReportMentionItem[];
  command: (item: ReportMentionItem) => void;
  clientRect?: (() => DOMRect | null) | null;
};

export type ReportMentionSuggestionListHandle = {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
};

export const ReportMentionSuggestionList = forwardRef<
  ReportMentionSuggestionListHandle,
  ReportMentionSuggestionListProps
>(function ReportMentionSuggestionList({ items, command, clientRect }, ref) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        setSelectedIndex((index) =>
          items.length === 0 ? 0 : (index + items.length - 1) % items.length
        );
        return true;
      }
      if (event.key === 'ArrowDown') {
        setSelectedIndex((index) =>
          items.length === 0 ? 0 : (index + 1) % items.length
        );
        return true;
      }
      if (event.key === 'Enter') {
        const item = items[selectedIndex];
        if (item) command(item);
        return true;
      }
      return false;
    },
  }));

  const rect = clientRect?.() ?? null;
  const style: CSSProperties = rect
    ? {
        position: 'fixed',
        left: Math.min(rect.left, window.innerWidth - 320),
        top: rect.bottom + 4,
        zIndex: 10050,
        width: 300,
      }
    : { display: 'none' };

  const resources = items.filter((item) => item.kind === 'resource');
  const actions = items.filter((item) => item.kind === 'action');
  const layers = items.filter((item) => item.kind === 'layer');

  const renderGroup = (
    title: string,
    group: ReportMentionItem[],
    indexOffset: number
  ) => {
    if (group.length === 0) return null;
    return (
      <div className="tw:py-1">
        <p className="tw:px-2 tw:pb-1 tw:text-[10px] tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
          {title}
        </p>
        {group.map((item, groupIndex) => {
          const absoluteIndex = indexOffset + groupIndex;
          return (
            <button
              key={`${item.kind}-${item.id}`}
              type="button"
              className={cn(
                'tw:flex tw:w-full tw:flex-col tw:items-start tw:gap-0.5 tw:px-2 tw:py-1.5 tw:text-left tw:text-xs tw:hover:bg-accent',
                absoluteIndex === selectedIndex && 'tw:bg-accent'
              )}
              onMouseEnter={() => setSelectedIndex(absoluteIndex)}
              // Prevent editor blur / suggestion exit before click applies the mention.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => command(item)}
            >
              <span className="tw:font-medium">{item.label}</span>
              {item.subtitle ? (
                <span className="tw:text-[11px] tw:text-muted-foreground">
                  {item.subtitle}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div
      className="tw:max-h-64 tw:overflow-y-auto tw:rounded-lg tw:border tw:border-border tw:bg-popover tw:text-popover-foreground tw:shadow-md"
      style={style}
      data-testid="report-mention-suggestions"
    >
      {items.length === 0 ? (
        <p className="tw:px-2 tw:py-2 tw:text-xs tw:text-muted-foreground">
          No matching resources, actions, or layers
        </p>
      ) : (
        <>
          {renderGroup('Resources', resources, 0)}
          {renderGroup('Actions', actions, resources.length)}
          {renderGroup('Layers', layers, resources.length + actions.length)}
        </>
      )}
    </div>
  );
});
